import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useActivateUserMutation,
  useAddUserFileMutation,
  useAdminUsersQuery,
  useDeactivateUserMutation,
  useDeleteUserFileMutation,
  useResetUserPasswordMutation,
  useUserByIdQuery,
} from 'api';
import { Form, notification, UploadProps } from 'components';
import { ErrorMappingValueType } from 'typings';
import { UserStatus } from 'models/User';
import { ADMIN_ROUTES } from 'constants/routes';
import { MAX_FILE_SIZE } from 'constants/index';

export const useAccount = () => {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const location = useLocation();
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');

  const { data: userDetails = {} } = useUserByIdQuery({ id });
  const { data: userData = [] } = useAdminUsersQuery();
  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [openActivateDialog, setOpenActivateDialog] = useState(false);
  const [form] = Form.useForm();
  const [addUserFile] = useAddUserFileMutation();
  const [deleteUserFile] = useDeleteUserFileMutation();
  const [resetUserPassword, { isLoading: isLoadingReset }] = useResetUserPasswordMutation();
  const [activateUser, { isLoading: activateLoading }] = useActivateUserMutation();
  const [deactivateUser, { isLoading: deactivateLoading }] = useDeactivateUserMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const isDeactivateVisible = useMemo(
    () => (userDetails.id && userId ? userDetails.id !== userId : false),
    [userDetails, userId]
  );
  const isUserActive = useMemo(
    () => userDetails.status === UserStatus.ACTIVE || userDetails.status === UserStatus.INVITED,
    [userDetails]
  );
  const filteredUserList = useMemo(
    () => userData.filter((_user: any) => _user.id !== userDetails.id && _user.status === UserStatus.ACTIVE),
    [userData, userDetails]
  );

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message:
              message === 'Valid reset key already exists'
                ? 'A password reset request has already been sent.'
                : (message as string),
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const handleAvatarChange: UploadProps['onChange'] = async (info) => {
    if (info?.fileList?.length) {
      const file = info?.file;
      if (file?.size && file.size > MAX_FILE_SIZE) {
        openNotificationWithIcon({ type: 'error', message: 'File exceeds the maximum size of 5 MB.' });
        return;
      }
      if (file) {
        const formData = new FormData();
        formData.append('adminId', id);
        formData.append('files', file as any, file.name);
        try {
          const fileResponse = await addUserFile(formData);
          if (fileResponse?.data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: 'Profile picture was successfully uploaded.',
            });
          } else {
            handleErrors(fileResponse.error);
          }
        } catch (err) {
          handleErrors(err);
        }
      }
    }
  };

  const handleAvatarDelete = useCallback(async () => {
    const [_, name] = userDetails.files.AVATAR.split('fileName=');

    try {
      const fileResponse = await deleteUserFile({ name, id });
      if (fileResponse?.data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'Profile picture was successfully deleted.',
        });
      } else {
        handleErrors(fileResponse.error);
      }
    } catch (err) {
      handleErrors(err);
    }
  }, [deleteUserFile, userDetails, id, openNotificationWithIcon, handleErrors]);

  const resetPassword = useCallback(async () => {
    try {
      const response = await resetUserPassword({ email: userDetails.email });
      if (response?.data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'The password reset email sent to the user. ',
        });
      } else {
        handleErrors(response.error);
      }
    } catch (err) {
      console.log(err, '=> err');
    } finally {
      setOpenResetPassword(false);
    }
  }, [userDetails, handleErrors, openNotificationWithIcon, resetUserPassword]);

  const activateAccount = useCallback(async () => {
    try {
      const response = await activateUser({ id });
      if (response?.data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'Account successfully reactivated',
        });
      } else {
        handleErrors(response.error);
      }
    } catch (err) {
      console.log(err, '=> err');
    } finally {
      setOpenActivateDialog(false);
    }
  }, [activateUser, id, openNotificationWithIcon, handleErrors]);

  const deactivateAccount = useCallback(async () => {
    const replacementId = form.getFieldValue('replacementId');
    try {
      const response = await deactivateUser({ id, replacementId });
      if (response?.data?.success) {
        openNotificationWithIcon({
          type: 'success',
          message: 'Account successfully deactivated',
        });
        if (userDetails.status === UserStatus.INVITED) {
          navigate(ADMIN_ROUTES.users.list);
        }
      } else {
        handleErrors(response.error);
      }
    } catch (err) {
      console.log(err, '=> err');
    } finally {
      setOpenActivateDialog(false);
    }
  }, [deactivateUser, id, openNotificationWithIcon, handleErrors, form, navigate, userDetails]);

  useEffect(() => {
    if (location.state?.notification) {
      const { type, message }: ErrorMappingValueType = location.state.notification;
      notificationApi[type]({ message });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, notificationApi, navigate]);

  return {
    userDetails,
    filteredUserList,
    handleAvatarDelete,
    handleAvatarChange,
    contextHolder,
    activateAccount,
    deactivateAccount,
    resetPassword,
    isLoadingReset,
    openResetPassword,
    openActivateDialog,
    setOpenActivateDialog,
    setOpenResetPassword,
    isUserActive,
    isDeactivateVisible,
    accountActivationLoading: activateLoading || deactivateLoading,
    form,
  };
};
